'use client';

import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef } from 'react';

interface Testimonial {
  text: string;
  name: string;
  role: string;
  company?: string;
  imageColor?: string;
  imageUrl?: string;
}

const testimonials: Testimonial[] = [
  {
    text: 'Konsolidasi Justisio memudahkan inventarisasi peraturan sehingga memangkas waktu saya dalam menyusun telaah hukum dan dokumen hukum lainnya',
    name: 'Angela Silvana',
    role: 'Analis Hukum Ahli Pertama',
    company: 'Badan Kepegawaian Negara',
    imageColor: 'bg-blue-200',
    imageUrl: '/static/images/landing/testimonials/angela-80.webp',
  },
  {
    text: 'Karena saya setiap hari harus menghadapi berbagai peraturan, ketika ada konsolidasi yang sangat nyaman dibaca bahkan sampai bisa bandingin sama pasal sebelum perubahan sangat membantu untuk riset hukum',
    name: 'M. Yusuf Adiputro S',
    role: 'Pengacara',
    imageColor: 'bg-orange-200',
    imageUrl: '/static/images/landing/testimonials/yusuf-80.webp',
  },
  {
    text: 'Tampilannya sangat nyaman, permasalahan dengan PDF aturan selama ini kelar, gak nyangka ternyata peraturan baca dan riset aturan jadi experience seperti ini',
    name: 'Rizqya Nafila Putri',
    role: 'Associate',
    company: 'Batavia Advocatorum',
    imageColor: 'bg-green-200',
    imageUrl: '/static/images/landing/testimonials/rizqya-80.webp',
  },
  {
    text: 'Justisio sangat membantu proses riset hukum dan penanganan perkara, terutama memastikan kesesuaian peraturan perundang-undangan. Fitur konsolidasi sangat efektif dan akurat.',
    name: 'Bilham Warmindi',
    role: 'Associate',
    company: 'Drawy & Droit Law Office',
    imageColor: 'bg-purple-200',
    imageUrl: '/static/images/landing/testimonials/bilham-80.webp',
  },
  {
    text: 'Justisio sangat membantu saya dalam proses mencari peraturan yang tepat dan memastikan keberlakuan peraturan sampai ditingkat pasal',
    name: 'Surya M. Adam',
    role: 'Associate',
    company: 'RAP & Co Law Office',
    imageColor: 'bg-teal-200',
    imageUrl: '/static/images/landing/testimonials/surya-80.webp',
  },
];

const duplicatedTestimonials = [...testimonials, ...testimonials];

const SCROLL_SPEED = 0.3; // px per frame (~18px/s at 60fps)
const MANUAL_PAUSE_DURATION = 4000;

interface TestimonialSectionProps {
  className?: string;
}

export function TestimonialSection({ className = '' }: TestimonialSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const manualPauseRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startAutoScroll = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    const animate = () => {
      if (!scrollRef.current) return;

      const { scrollLeft, scrollWidth } = scrollRef.current;
      const halfWay = scrollWidth / 2;

      if (scrollLeft >= halfWay) {
        scrollRef.current.scrollLeft = scrollLeft - halfWay;
      }

      if (!manualPauseRef.current) {
        scrollRef.current.scrollLeft += SCROLL_SPEED;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    startAutoScroll();
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, [startAutoScroll]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const card = scrollRef.current.querySelector('[data-testimonial-card]') as HTMLElement | null;
    if (!card) return;
    const scrollAmount = card.offsetWidth + 24;

    const { scrollLeft, scrollWidth } = scrollRef.current;
    const halfWay = scrollWidth / 2;

    let newScrollLeft: number;
    if (direction === 'left') {
      newScrollLeft = scrollLeft - scrollAmount;
      if (newScrollLeft < 0) {
        newScrollLeft = halfWay + newScrollLeft;
      }
    } else {
      newScrollLeft = scrollLeft + scrollAmount;
      if (newScrollLeft >= halfWay) {
        newScrollLeft = newScrollLeft - halfWay;
      }
    }

    scrollRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });

    manualPauseRef.current = true;
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      manualPauseRef.current = false;
    }, MANUAL_PAUSE_DURATION);
  };

  return (
    <section
      className={`py-12 md:py-16 px-4 md:px-6 bg-white ${className}`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-fluid-3xl md:text-fluid-4xl lg:text-fluid-4xl font-bold text-neutral-900 max-w-4xl mx-auto tracking-tight">
            Dipercaya Oleh Profesional Hukum
          </h2>
        </div>

        <div className="relative">
          <div className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10">
            <button
              onClick={() => scroll('left')}
              aria-label="Geser testimoni ke kiri"
              className="w-12 h-12 bg-white border-2 border-neutral-200 rounded-full flex items-center justify-center hover:border-neutral-900 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 transition-all duration-200"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
            <button
              onClick={() => scroll('right')}
              aria-label="Geser testimoni ke kanan"
              className="w-12 h-12 bg-white border-2 border-neutral-200 rounded-full flex items-center justify-center hover:border-neutral-900 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 transition-all duration-200"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide px-[calc(50vw-45vw)] md:px-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {duplicatedTestimonials.map((item, idx) => (
              <TestimonialCard key={idx} {...item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({
  text,
  name,
  role,
  company,
  imageColor = 'bg-blue-200',
  imageUrl,
}: Testimonial) {
  return (
    <div data-testimonial-card className="shrink-0 w-[90vw] md:w-[380px] bg-white border-2 border-neutral-200 rounded-xl p-6 hover:border-neutral-900 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 flex flex-col">
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} size={16} className="text-yellow-500 fill-yellow-500" />
        ))}
      </div>

      <p className="text-neutral-600 leading-relaxed flex-1 mb-6">"{text}"</p>

      <div className="flex items-center gap-4 pt-4 border-t border-neutral-100">
        {imageUrl ? (
          <div className="w-20 h-20 shrink-0 rounded-full overflow-hidden relative border border-neutral-100/50">
            <Image src={imageUrl} alt={name} fill sizes="80px" className="object-cover" />
          </div>
        ) : (
          <div
            className={`w-20 h-20 shrink-0 rounded-full ${imageColor} flex items-center justify-center font-bold text-neutral-700 text-xl`}
          >
            {name.charAt(0)}
          </div>
        )}
        <div>
          <p className="font-semibold text-neutral-900 text-base">{name}</p>
          <p className="text-sm text-neutral-500 leading-snug">
            {role}
            {company ? `, ${company}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
